/**
 * Credits panel — slow-scrolling credits list.
 */

export interface CreditsPanelCallbacks {
  onClose: () => void;
}

interface CreditSection {
  heading: string;
  lines: string[];
}

const CREDITS: CreditSection[] = [
  {
    heading: 'THREADBARE',
    lines: ['A visual novel about what the city chooses to remember.'],
  },
  {
    heading: 'Story & Writing',
    lines: ['Original story, world, and characters'],
  },
  {
    heading: 'Engine',
    lines: [
      'Vanilla TypeScript — no framework',
      'Vite 5 · Zod · Vitest',
      'AudioContext BGM engine with crossfade',
      'Parallax background layering',
      'Typewriter renderer · Backlog · Save / Load',
    ],
  },
  {
    heading: 'Art Direction',
    lines: [
      'ComfyUI + animagine-xl-3.1',
      'Character design — 5 principals + 11 world characters',
      'Hand correction pipeline — MeshGraphormer + ControlNet',
      '24 background locations across 3 acts',
    ],
  },
  {
    heading: 'Music',
    lines: [
      '38 original BGM tracks',
      'Composed and produced for THREADBARE',
      'Scene-mapped audio engine with mood resolution',
    ],
  },
  {
    heading: 'Cast',
    lines: [
      'Sable Venn',
      'Loom Ashara',
      'Rue Castigan',
      'Maren Vey',
      'Wick',
      'Madame Coil · Spindle Halix · Reader Petrichor · Eira Indigo',
      'Vela · Erren · Marin · and the voices of Verath',
    ],
  },
];

/**
 * Render the credits overlay with slow-scroll animation.
 */
export function renderCreditsPanel(
  root: HTMLElement,
  callbacks: CreditsPanelCallbacks,
): void {
  // Remove any existing credits
  const existing = root.querySelector('#credits-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'credits-modal';

  const scroller = document.createElement('div');
  scroller.className = 'credits-scroller';

  // Build credit sections
  for (const section of CREDITS) {
    if (section.heading) {
      const h = document.createElement('h3');
      h.className = 'credits-heading';
      h.textContent = section.heading;
      scroller.appendChild(h);
    }

    for (const line of section.lines) {
      const p = document.createElement('p');
      p.className = 'credits-line';
      p.textContent = line;
      scroller.appendChild(p);
    }

    // Spacer between sections
    const spacer = document.createElement('div');
    spacer.className = 'credits-spacer';
    scroller.appendChild(spacer);
  }

  // Thank you
  const thanks = document.createElement('p');
  thanks.className = 'credits-thanks';
  thanks.textContent = 'Thank you for playing.';
  scroller.appendChild(thanks);

  overlay.appendChild(scroller);

  // Close button (top-right)
  const closeBtn = document.createElement('button');
  closeBtn.className = 'credits-close';
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => close());
  overlay.appendChild(closeBtn);

  // Esc to close
  const keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', keyHandler);

  // Click overlay to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  root.appendChild(overlay);

  function close(): void {
    document.removeEventListener('keydown', keyHandler);
    overlay.remove();
    callbacks.onClose();
  }
}
